/**
 * PartsCounterCard Component
 *
 * Main container for the Parts Counter Performance dashboard.
 * Includes the sortable person list and pie chart visualization.
 * Green theme to distinguish from SA (orange) and Tech (teal).
 */

import React from 'react';
import {
  UserGroupIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner } from '../../LoadingSpinner';
import {
  PartsCounterPerson,
  PartsCounterSummary,
  SortConfig,
} from './types';
import { PartsCounterList } from './PartsCounterList';
import { PartsCounterPieChart } from './PartsCounterPieChart';

interface PartsCounterCardProps {
  persons: PartsCounterPerson[];
  summary: PartsCounterSummary | null;
  loading: boolean;
  refreshing?: boolean;
  error: string | null;
  year: number;
  month: number;
  sortConfig: SortConfig;
  onSortChange: (config: SortConfig) => void;
  onRefresh?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  showHeaderTotals?: boolean;
}

function getMonthName(month: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return months[month - 1] || '';
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export const PartsCounterCard: React.FC<PartsCounterCardProps> = ({
  persons,
  summary,
  loading,
  refreshing = false,
  error,
  year,
  month,
  sortConfig,
  onSortChange,
  onRefresh,
  collapsed = false,
  onToggleCollapse,
  showHeaderTotals = true,
}) => {
  const totalGross = summary?.totalGross ??
    persons.reduce((sum, p) => sum + p.grossTotal, 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div
        className={`px-4 py-3 ${collapsed ? '' : 'border-b border-gray-200'} ${onToggleCollapse ? 'cursor-pointer select-none hover:bg-gray-50 transition-colors' : ''}`}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Parts Counter Performance
              </h3>
              <p className="text-sm text-gray-500">
                {getMonthName(month)} {year} - Gross by Counter Person
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {!collapsed && onRefresh && (
              <button
                onClick={(e) => { e.stopPropagation(); onRefresh(); }}
                disabled={loading || refreshing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                title="Refresh parts counter data from DealerBuilt"
              >
                <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            )}
            {!collapsed && showHeaderTotals && summary && (
              <div className="text-right">
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(totalGross)}
                </p>
                <p className="text-sm text-gray-500">
                  Total Parts Gross
                </p>
                <div className="flex items-center justify-end gap-3 mt-1 text-sm text-gray-500">
                  <span>{summary.totalTransactions} Transactions</span>
                  <span className="text-gray-300">|</span>
                  <span>{summary.totalLostSales} Lost Sales</span>
                </div>
              </div>
            )}
            {onToggleCollapse && (
              <ChevronDownIcon
                className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
              />
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {!collapsed && <div className="p-4">
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <LoadingSpinner />
            <p className="mt-4 text-gray-600">Loading parts counter data...</p>
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-red-800 font-medium">
                  Error Loading Data
                </h4>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && (
          <>
            {persons.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <UserGroupIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p>No parts counter data available for this period.</p>
                <p className="text-sm mt-1">
                  Data is loaded nightly from DealerBuilt.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <PartsCounterList
                    persons={persons}
                    sortConfig={sortConfig}
                    onSortChange={onSortChange}
                  />
                </div>
                <div className="lg:col-span-1">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 text-center">
                      Gross Distribution
                    </h4>
                    <PartsCounterPieChart
                      persons={persons}
                      totalGross={totalGross}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>}

      {/* Footer - Last Updated */}
      {!collapsed && summary?.lastUpdated && !loading && (
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg">
          <p className="text-xs text-gray-500 text-center">
            Last updated:{' '}
            {new Date(summary.lastUpdated).toLocaleString('en-US', {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </p>
        </div>
      )}
    </div>
  );
};
